export type ParentStatus = 'draft' | 'active' | 'suspended';
export type Direction = 'inbound' | 'outbound';
export type Severity = 'error' | 'warning' | 'advisory';

export type FailureMode =
  | 'fail_closed'
  | 'fail_open_debt_blue'
  | 'fail_open_phantom'
  | 'fail_open_compensated'
  | 'not_applicable';

export const FILE_TYPE_KEYS = [
  'client_table',
  'creditor_table',
  'transaction_table',
  'eligibility_response',
  'ready_to_settle',
  'settlement_request',
  'settlement_confirmation',
  'funding_receipt',
  'creditor_paid',
  'escrow_sweep',
] as const;

export type FileTypeKey = (typeof FILE_TYPE_KEYS)[number];

export interface Principal {
  email: string;
  display_name: string;
  role: 'admin' | 'viewer';
}

export interface AdminUserRow {
  email: string;
  display_name: string;
  role: 'admin' | 'viewer';
  active: boolean;
  mfa_enrolled: boolean;
  last_login_at: string | null;
  locked_until: string | null;
}

export interface AuditEntry {
  entry_id: string;
  actor: string;
  action: string;
  entity_type: string;
  entity_id: string;
  at: string;
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
  source_ip?: string | null;
}

export interface Buckets {
  prod: string;
  qa: string | null;
  iam_policy_name?: string | null;
  iam_user_name?: string | null;
  eventbridge_enabled: boolean;
}

export interface Brand {
  brand_key: string;
  dsp_partner_value: string;
  zoho_partner_id: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  address?: string | null;
  is_primary: boolean;
}

export interface FileTypeConfig {
  file_type_key: FileTypeKey;
  direction: Direction;
  folder: string;
  filename_stem: string;
  filename_template: string;
  include_token: boolean;
  enabled: boolean;
  immutable: boolean;
  immutable_reason: string | null;
}

export interface SettlementTiming {
  first_settlement_days: number;
  last_settlement_days: number;
  legal_multiplier: number;
}

export interface Integration {
  integration_key: string;
  kind: 'webhook' | 'sftp' | 'api';
  enabled: boolean;
  url_secret_ref: string | null;
  event_type: string | null;
  timeout_seconds: number;
  allowed_buckets: string[];
  notes: string;
}

export interface DspParent {
  parent_key: string;
  display_name: string;
  dsp_parent_company_value: string;
  filename_token: string;
  aliases: string[];
  status: ParentStatus;
  seeded_from_preset: string;
  website: string | null;
  buckets: Buckets;
  brands: Brand[];
  file_types: Record<string, FileTypeConfig>;
  snowflake_profile_key: string | null;
  settlement_timing: SettlementTiming;
  integrations: Integration[];
  lendapi_export_enabled: boolean;
  /** False for lead-only parents such as MLG, which send no settlement requests
   *  and are therefore exempt from the required settlement bindings. */
  settlement_enabled: boolean;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface ParentSummary {
  parent_key: string;
  display_name: string;
  dsp_parent_company_value: string;
  filename_token: string;
  status: ParentStatus;
  bucket_prod: string;
  bucket_qa: string | null;
  brand_count: number;
  file_type_count: number;
  snowflake_profile_key: string | null;
  required_bindings_complete: boolean;
  missing_required_bindings: string[];
}

export interface LambdaBinding {
  parent_key: string;
  lambda_key: string;
  enabled: boolean;
  file_type_keys: FileTypeKey[];
  eventbridge_enabled: boolean;
  eventbridge_prefix: string | null;
  filename_stem_override: string | null;
  bucket_override: string | null;
  zoho_partner_ids: string[];
  snowflake_profile_key: string | null;
  webhook_enabled: boolean;
  integration_key: string | null;
  extra: Record<string, unknown>;
  notes: string;
  updated_at: string;
}

export interface IoRef {
  kind: 'file' | 'zoho' | 'snowflake' | 'api';
  key: string;
  direction: string;
}

export type OpsArea = 'settlement_operations' | 'lead_scoring' | 'general';

export interface OpsAreaMeta {
  title: string;
  subtitle: string;
  blurb: string;
}

export interface DspUsage {
  parent_key: string;
  display_name: string;
  parent_status: ParentStatus;
  parent_company: string;
  status: 'active' | 'disabled' | 'not_applicable';
  narrative: string;
  config_used: {
    enabled: boolean;
    bucket: string | null;
    file_type_keys: string[];
    eventbridge_prefix: string | null;
    snowflake_profile_key: string | null;
    zoho_partner_ids: string[];
    webhook_enabled: boolean;
    integration_key: string | null;
    extra: Record<string, unknown>;
    resolved_keys: KeyPreview[];
  };
  how_runtime_reads: string[];
}

export interface LambdaRegistry {
  lambda_key: string;
  display_name: string;
  repo: 'settlement' | 'lead';
  ops_area: OpsArea;
  code_path: string;
  trigger_type: string;
  trigger_detail: Record<string, unknown>;
  operation_summary: string;
  steps: string[];
  inputs: IoRef[];
  outputs: IoRef[];
  fail_closed_rules: string[];
  failure_mode: FailureMode;
  legacy_env_vars: string[];
  terraform_vars: string[];
  iam_summary: string;
  danger_flags: string[];
  required_for_active_parent: boolean;
  partner_aware: boolean;
  status: 'active' | 'deprecated';
  runbook_url: string | null;
  notes: string;
}

export interface LambdaSummary {
  lambda_key: string;
  display_name: string;
  repo: 'settlement' | 'lead';
  ops_area: OpsArea;
  ops_area_title: string;
  code_path: string;
  trigger_type: string;
  trigger_summary: string;
  operation_summary: string;
  partners_enabled: string[];
  config_completeness: number;
  failure_mode: FailureMode;
  danger_flag_count: number;
  partner_aware: boolean;
  required_for_active_parent: boolean;
  status: 'active' | 'deprecated';
}

export interface KeyPreview {
  file_type_key: string;
  s3_uri?: string;
  error?: string;
}

export interface EffectiveConfig {
  enabled: boolean;
  bucket?: string;
  bucket_qa?: string | null;
  bucket_source?: string;
  snowflake_profile_key?: string | null;
  snowflake_source?: string;
  zoho_partner_ids?: string[];
  zoho_source?: string;
  filename_stems?: Record<string, string | null>;
  eventbridge?: {
    enabled: boolean;
    prefix: string | null;
    bucket_eventbridge_enabled: boolean;
  };
  failure_mode?: FailureMode;
}

export interface LambdaBindingRow {
  parent_key: string;
  display_name: string;
  parent_status: ParentStatus;
  binding: LambdaBinding | null;
  previews: KeyPreview[];
  effective: EffectiveConfig;
}

export interface LambdaDetail {
  registry: LambdaRegistry;
  ops_area: OpsArea;
  ops_area_meta: OpsAreaMeta;
  trigger_summary: string;
  config_completeness: number;
  bindings: LambdaBindingRow[];
  dsp_usage: DspUsage[];
  unbound_active_parents: string[];
}

export interface ParentLambdaRow {
  lambda_key: string;
  display_name: string;
  repo: 'settlement' | 'lead';
  trigger_type: string;
  failure_mode: FailureMode;
  partner_aware: boolean;
  status: 'active' | 'deprecated';
  required: boolean;
  danger_flag_count: number;
  direction: string;
  binding: LambdaBinding | null;
  binding_status: 'complete' | 'disabled' | 'missing' | 'unbound' | 'not_applicable';
  previews: KeyPreview[];
}

export interface ValidationIssue {
  severity: Severity;
  code: string;
  message: string;
  parent_key: string | null;
  lambda_key: string | null;
}

export interface ValidationResult {
  ok: boolean;
  issues: ValidationIssue[];
}

export interface SnowflakeProfile {
  profile_key: string;
  database: string;
  schema_name: string;
  role: string;
  warehouse: string;
  client_table: string | null;
  creditor_table: string | null;
  txn_table: string | null;
  transactions_cleaned_view: string | null;
  history_table: string | null;
  client_view_ungated: string | null;
  creditor_view_ungated: string | null;
  escrow_balance_col: string;
  draft_amount_col: string;
  sub_type_col: string | null;
  status_col: string | null;
  ds_acc_col: string;
  ds_routing_col: string;
  gateway_col: string;
  pii_allowlist: Record<string, string[]>;
  exists_in_snowflake: boolean;
  notes: string;
}

export interface Publication {
  version: number;
  published_at: string;
  published_by: string;
  s3_key: string;
  checksum_sha256: string;
  parent_count: number;
  lambda_count: number;
  binding_count: number;
  note: string;
}

export interface Dashboard {
  counts: {
    parents_total: number;
    parents_active: number;
    parents_draft: number;
    lambdas_total: number;
    lambdas_partner_aware: number;
  };
  missing_bindings: {
    lambda_key: string;
    display_name: string;
    required: boolean;
    unbound_parents: string[];
    config_completeness: number;
  }[];
  dangerous_fallbacks: {
    lambda_key: string;
    display_name: string;
    failure_mode: FailureMode;
    danger_flags: string[];
  }[];
  eventbridge_gaps: {
    parent_key: string;
    display_name: string;
    bucket_eventbridge_enabled: boolean;
    missing_watchers: string[];
  }[];
  latest_publication: Publication | null;
}
